import { useCallback, useEffect, useRef, useState } from 'react';
import { type PendingAttachment, validateFile, isImageMime, MAX_ATTACHMENTS_PER_MESSAGE } from './pendingAttachment';
import { signedFetch } from '../../utils/signedFetch';

interface UseAttachmentsOptions {
  apiUrl: string;
  tenantId: string | null;
  conversationId: string | null;
}

let nextLocalId = 0;
const makeLocalId = (): string => `att-${Date.now()}-${nextLocalId++}`;

/**
 * Manages composer attachments end-to-end: optimistic add, background upload,
 * status updates, removal, retry, and aborting in-flight uploads on unmount.
 */
export const useAttachments = ({ apiUrl, tenantId, conversationId }: UseAttachmentsOptions) => {
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Track AbortControllers and previewUrls so we can clean up — prevents
  // memory leaks (object URLs) and orphan requests when the component unmounts.
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const previewUrlsRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    // Snapshot the refs at mount so the cleanup function reads the same maps
    // we registered into — refs may have been replaced by the time cleanup runs.
    const controllers = controllersRef.current;
    const previewUrls = previewUrlsRef.current;
    return () => {
      controllers.forEach((c) => c.abort());
      previewUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  const removeAttachment = useCallback((localId: string) => {
    const controller = controllersRef.current.get(localId);
    if (controller) {
      controller.abort();
      controllersRef.current.delete(localId);
    }
    const preview = previewUrlsRef.current.get(localId);
    if (preview) {
      URL.revokeObjectURL(preview);
      previewUrlsRef.current.delete(localId);
    }
    setAttachments((prev) => prev.filter((a) => a.localId !== localId));
  }, []);

  const clearAttachments = useCallback(() => {
    controllersRef.current.forEach((c) => c.abort());
    controllersRef.current.clear();
    previewUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    previewUrlsRef.current.clear();
    setAttachments([]);
    setGlobalError(null);
  }, []);

  const startUpload = useCallback(
    async (localId: string, file: File) => {
      if (!tenantId) {
        setAttachments((prev) =>
          prev.map((a) => (a.localId === localId ? { ...a, status: 'error', errorMessage: 'No tenant selected' } : a))
        );
        return;
      }

      const controller = new AbortController();
      controllersRef.current.set(localId, controller);

      const readJsonMessage = async (response: Response, fallback: string): Promise<string> => {
        try {
          const data = (await response.json()) as { message?: string };
          return data.message ?? fallback;
        } catch {
          return fallback;
        }
      };

      const markError = (message: string): void => {
        setAttachments((prev) =>
          prev.map((a) => (a.localId === localId ? { ...a, status: 'error', errorMessage: message } : a))
        );
      };

      // Two-step presigned-PUT flow. The browser cannot send multipart
      // bodies > 6 MB through CloudFront → core-fn (BUFFERED Lambda Function
      // URL request cap), so:
      //   1. POST /chat-attachments/presign for an S3 PUT URL
      //   2. PUT the bytes directly to S3 (bypasses CloudFront + the cap)
      // There is no /complete: document → markdown extraction is performed
      // lazily inside chat-fn the first time the attachment is referenced,
      // then cached on the row for subsequent turns.
      const contentType = file.type || 'application/octet-stream';

      try {
        const presignResp = await signedFetch(`${apiUrl}/chat-attachments/presign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: controller.signal,
          body: JSON.stringify({
            tenantId,
            conversationId: conversationId ?? undefined,
            filename: file.name,
            mimeType: contentType,
            sizeBytes: file.size
          })
        });

        if (!presignResp.ok) {
          markError(await readJsonMessage(presignResp, `Upload failed (${presignResp.status})`));
          return;
        }

        const { attachmentId, putUrl } = (await presignResp.json()) as { attachmentId: string; putUrl: string };

        const putResp = await fetch(putUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': contentType },
          signal: controller.signal
        });
        if (!putResp.ok) {
          markError(`Upload failed (${putResp.status})`);
          return;
        }

        setAttachments((prev) =>
          prev.map((a) => (a.localId === localId ? { ...a, status: 'ready', remoteId: attachmentId } : a))
        );
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return;
        markError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        controllersRef.current.delete(localId);
      }
    },
    [apiUrl, tenantId, conversationId]
  );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      if (fileArray.length === 0) return;

      const errors: string[] = [];
      const accepted: { localId: string; file: File; kind: 'image' | 'document'; previewUrl?: string }[] = [];

      // Compute the per-add slot allotment up front; "currently held" includes
      // any chips already on screen so the cap accounts for the running total.
      let remainingSlots = MAX_ATTACHMENTS_PER_MESSAGE - attachments.length;

      for (const file of fileArray) {
        if (remainingSlots <= 0) {
          errors.push(`Cannot attach more than ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`);
          break;
        }
        const v = validateFile(file);
        if (!v.ok) {
          errors.push(`${file.name}: ${v.reason}`);
          continue;
        }
        const localId = makeLocalId();
        const kind: 'image' | 'document' = isImageMime(file.type) ? 'image' : 'document';
        let previewUrl: string | undefined;
        if (kind === 'image') {
          previewUrl = URL.createObjectURL(file);
          previewUrlsRef.current.set(localId, previewUrl);
        }
        accepted.push({ localId, file, kind, previewUrl });
        remainingSlots -= 1;
      }

      if (accepted.length > 0) {
        setAttachments((prev) => [
          ...prev,
          ...accepted.map(({ localId, file, kind, previewUrl }) => ({
            localId,
            file,
            status: 'uploading' as const,
            kind,
            previewUrl
          }))
        ]);
        for (const a of accepted) {
          void startUpload(a.localId, a.file);
        }
      }

      setGlobalError(errors.length > 0 ? errors.join(' ') : null);
    },
    [attachments.length, startUpload]
  );

  const retryUpload = useCallback(
    (localId: string) => {
      const att = attachments.find((a) => a.localId === localId);
      if (!att) return;
      setAttachments((prev) =>
        prev.map((a) => (a.localId === localId ? { ...a, status: 'uploading', errorMessage: undefined } : a))
      );
      void startUpload(localId, att.file);
    },
    [attachments, startUpload]
  );

  const isUploading = attachments.some((a) => a.status === 'uploading');
  const readyAttachmentIds = attachments
    .filter((a): a is PendingAttachment & { remoteId: string } => a.status === 'ready' && !!a.remoteId)
    .map((a) => a.remoteId);

  return {
    attachments,
    addFiles,
    removeAttachment,
    retryUpload,
    clearAttachments,
    isUploading,
    readyAttachmentIds,
    globalError
  };
};
